import { object, bool } from "prop-types";
import { ResponsiveImage } from "@quintype/components";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "@quintype/components";

import ScrollSnap from "../../../molecules/ScrollSnap";
import TriangleArrow from "../../../atoms/icons/triangle-arrow";
import SingleStrokeRightArrow from "../../../atoms/icons/single-stroke-right-arrow";

import "./slideshow-element.m.css";
import { pushEventToDataLayer } from "../../../helper/datalayer-utils";

const Slideshow = ({ story, element, auto = false, NewBlock = false, inStory = false, textColor = null }) => {
  const [arrowTop, setArrowTop] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);
  const isPaused = useRef(false);
  const slideshowWrapper = useRef(null);
  const items = element?.["story-elements"] || [];
  const slideCount = items.length;
  const storySlugLinking = NewBlock && !inStory;
  const isLiveBlog = story?.["story-template"] === "live-blog";

  const handlePageView = useCallback(() => {
    if (window?.dataLayer) pushEventToDataLayer("slideShow");
  }, []);

  const getImageUrl = (item) => {
    const rawKey = item["image-s3-key"];

    const encodedKey = rawKey.includes("%2F")
      ? rawKey
      : encodeURIComponent(rawKey);

    return `https://media.assettype.com/${encodedKey}?w=700&auto=format%2Ccompress&fit=max`;
  };

  useEffect(() => {
    if (!items.length) return;

    const indexesToPreload = [
      (currentIndex + 1) % slideCount,
      (currentIndex + 2) % slideCount,
    ];

    indexesToPreload.forEach((idx) => {
      const item = items[idx];
      if (!item) return;

      const img = new Image();
      img.src = getImageUrl(item);
    });
  }, [currentIndex]);

  const calculateArrowTop = useCallback(() => {
    const ImageHeight = slideshowWrapper.current.offsetHeight || 0;
    const arrowHeight = NewBlock ? (window.innerWidth < 768 ? 35 : 48) : window.innerWidth < 768 ? 50 : 90;
    setArrowTop((ImageHeight - arrowHeight) / 2);
  }, [NewBlock]);


  useEffect(() => {
    if (typeof window !== "undefined") {
      calculateArrowTop();
      window.addEventListener("resize", calculateArrowTop);
      return () => window.removeEventListener("resize", calculateArrowTop);
    }
  }, [calculateArrowTop]);

  // autoplay helpers
  const startAuto = useCallback(() => {
    if (!auto || slideCount <= 1 || isPaused.current) return;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = (prev + 1) % slideCount;
        handlePageView();
        return next;
      });
    }, 5000);
  }, [auto, slideCount, handlePageView]);

  const pauseAuto = useCallback(() => {
    isPaused.current = true;
    clearInterval(intervalRef.current);
  }, []);

  const resumeAuto = useCallback(() => {
    isPaused.current = false;
    if (auto) {
      startAuto();
    }
  }, [auto, startAuto]);


  useEffect(() => {
    if (auto && slideCount > 1) startAuto();
    return () => clearInterval(intervalRef.current);
  }, [auto, slideCount, startAuto]);

  // when parent-controlled index changes (from interval or user swipe), restart autoplay
  const handleIndexChange = (index) => {
    setCurrentIndex(index);
    if (auto && !isPaused.current) {
      // restart timer so user interaction resets the 5s timer
      clearInterval(intervalRef.current);
      startAuto();
    }
  };

  const children = items.map((item, index) => (
    <div key={index}>
      <div className="slideshow-image-wrapper" ref={slideshowWrapper}>

        <figure className="blank-story-image" styleName="qt-image-16x9">
          {storySlugLinking || item?.hyperlink ? (
            <Link href={storySlugLinking ? `/${story?.slug}` : item?.hyperlink}>
              <ResponsiveImage
                slug={decodeURIComponent(item["image-s3-key"])}
                metadata={item["image-metadata"]}
                aspectRatio={[16, 9]}
                widths={NewBlock ? [400, 700] : [125, 240, 320]}
                imgParams={{ auto: ["format", "compress"], fit: "max" }}
                alt={item["image-attribution"] || item.title || ""}
                {...(NewBlock ? { loading: "lazy", lazy: true } : {})}
              />
            </Link>
          ) : (
            <ResponsiveImage
              slug={decodeURIComponent(item["image-s3-key"])}
              metadata={item["image-metadata"]}
              aspectRatio={[16, 9]}
              widths={NewBlock ? [400, 700] : [125, 240, 320]}
              imgParams={{ auto: ["format", "compress"], fit: "max" }}
              alt={item["image-attribution"] || item.title || ""}
              {...(NewBlock ? { loading: "lazy", lazy: true } : {})}
            />
          )}
        </figure>
      </div>
      {item.title && !NewBlock && (
        <div styleName="scroll-caption-wrapper">
          <figcaption styleName="caption" dangerouslySetInnerHTML={{ __html: item.title }} />
          {item["image-attribution"] && (
            <span styleName="attribute" dangerouslySetInnerHTML={{ __html: item["image-attribution"] }} />
          )}
        </div>
      )}
    </div>
  ));

  return (
    <div styleName="wrapper" onMouseEnter={pauseAuto} onMouseLeave={resumeAuto}>
      <ScrollSnap
        activeIndex={currentIndex}
        onChange={handleIndexChange}
        leftArrow={
          <div className={`arrow-wrapper ${NewBlock && !inStory ? "hide-mobile" : ""}`} styleName={`${NewBlock ? "newBlock-arrow" : ""}`} style={{ top: arrowTop, left: `${NewBlock ? "-6px" : "0px"}`, position: "absolute", ...(NewBlock ? { padding: "0px 4px 0px 0px" } : {}) }}>
            {NewBlock ? <SingleStrokeRightArrow /> : <TriangleArrow />}
          </div>
        }
        rightArrow={
          <div className={`arrow-wrapper ${NewBlock && !inStory ? "hide-mobile" : ""}`} styleName={`${NewBlock ? "newBlock-arrow" : ""}`} style={{ top: arrowTop, right: `${NewBlock ? "-6px" : "0px"}`, position: "absolute", ...(NewBlock ? { padding: "0px 0px 0px 4px" } : {}) }}>
            {NewBlock ? <SingleStrokeRightArrow /> : <TriangleArrow />}
          </div>
        }
        handleNextClick={handlePageView}
        NewBlock={NewBlock}
        infinite={true}
      >

        {children}
      </ScrollSnap>

      {NewBlock && slideCount > 1 && (
        <div styleName={`dots-wrapper ${(NewBlock && inStory && isLiveBlog) ? "dots-lb-wrapper" : "" }`}>
          {items.map((_, idx) => (
            <div
              key={idx}
              styleName={idx === currentIndex ? "dot active" : "dot"}
              style={inStory ? {} : { backgroundColor: textColor ? textColor : "white" }}
              onClick={() => handleIndexChange(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

Slideshow.propTypes = {
  story: object,
  element: object,
  auto: bool,
  NewBlock: bool,
  inStory: bool,
};

export default Slideshow;
